/**
 * DataList cell renderers and row-click handler shared by every extranet
 * table, registered once on import with the singleton
 * `dataListWorkflowRegistry` (desktop rows AND the < 768px mobile cards use
 * the same renderers).
 *
 * - {@link EXTRANET_DATE_RENDERER}: a VEOS date — ms timestamp → `JJ/MM/AAAA`
 *   (Paris calendar date), pre-formatted string as-is, `null` → empty. The
 *   column keeps the raw value, so a client-side sort on a ms column sorts
 *   chronologically.
 * - {@link registerStatusBadgeRenderer}: a column rendered as a
 *   {@link StatusBadge} from a declarative config (one renderer key per
 *   config).
 * - {@link EXTRANET_ROW_OPEN_HANDLER}: row click → the row's own `__ctx.onOpen`
 *   (per-instance navigation without module-global state — the claimflow
 *   `__ctx` convention).
 */

import { dataListWorkflowRegistry } from '@components/integrated/data-list';

import { formatVeosDate, type VeosScalar } from '../../utils/format';
import { StatusBadge } from './StatusBadge';
import type { StatusBadgeConfig } from './statusBadgeConfig';

/** Renderer key of VEOS date columns (`type: 'custom'`). */
export const EXTRANET_DATE_RENDERER = 'extranet-veosDate';

/** Row-click handler key: calls the row's `__ctx.onOpen`. */
export const EXTRANET_ROW_OPEN_HANDLER = 'extranet-rowOpen';

/** Per-row context carried under `__ctx` by clickable extranet rows. */
export interface ExtranetRowContext {
  onOpen?: () => void;
}

/** Reads the `__ctx` of a DataList row. */
export function getExtranetRowContext(
  row: Record<string, unknown>,
): ExtranetRowContext | undefined {
  const ctx = row.__ctx;
  return typeof ctx === 'object' && ctx !== null ? (ctx as ExtranetRowContext) : undefined;
}

dataListWorkflowRegistry.registerCellRenderer(EXTRANET_DATE_RENDERER, (value) =>
  formatVeosDate(value as VeosScalar),
);

dataListWorkflowRegistry.registerRowClickHandler(EXTRANET_ROW_OPEN_HANDLER, (row) => {
  getExtranetRowContext(row)?.onOpen?.();
});

/**
 * Register a cell renderer that shows the cell value as a {@link StatusBadge}
 * built from `config`. Idempotent per key (re-registering replaces it).
 *
 * @returns the renderer key, for `typeOptions: { renderer }`.
 */
export function registerStatusBadgeRenderer(key: string, config: StatusBadgeConfig): string {
  dataListWorkflowRegistry.registerCellRenderer(key, (value) => (
    <StatusBadge value={value as string | number | null | undefined} config={config} />
  ));
  return key;
}
